import React from 'react';

import UniversalGridder from '$styles/universal-gridder';
import {
  MosaicFeaturesSelf,
  MosaicItem,
  TileFeature,
  TileNumber
} from '$components/mosaic';
import { getAttributionObj, hasOnlyChildrenOfType } from './utils';
import { ErrorBlock, ErrorCode } from './error';

function FeatureList(props: { children: React.ReactNode }) {
  const { children } = props;

  if (!hasOnlyChildrenOfType(children, ['FeatureListItem'])) {
    return (
      <ErrorBlock>
        <p>
          The <strong>FeatureList</strong> block requires{' '}
          <strong>FeatureListItem</strong> as children. For example:
        </p>
        <div>
          <ErrorCode>
            {`<FeatureList>
              <FeatureList.Item>Something</FeatureList.Item>
            </FeatureList>`}
          </ErrorCode>
        </div>
      </ErrorBlock>
    );
  }

  return (
    <MosaicFeaturesSelf>
      <UniversalGridder as='ul'>{children}</UniversalGridder>
    </MosaicFeaturesSelf>
  );
}

interface FeatureListItemProps {
  title: string;
  src: string | { default: string };
  attributionName?: string;
  attributionUrl?: string;
  alt: string;
  children: React.ReactNode;
}

function FeatureListItem({
  title,
  src,
  attributionName,
  attributionUrl,
  alt,
  children
}: FeatureListItemProps) {
  return (
    <MosaicItem>
      <TileFeature
        title={title}
        media={{
          src: typeof src === 'string' ? src : src?.default,
          attribution: getAttributionObj({ attributionName, attributionUrl }),
          alt
        }}
      >
        {children}
      </TileFeature>
    </MosaicItem>
  );
}

FeatureListItem.displayName = 'FeatureListItem';

FeatureList.Item = FeatureListItem;

function NumberList(props: { children: React.ReactNode }) {
  const { children } = props;

  if (!hasOnlyChildrenOfType(children, ['NumberListItem'])) {
    return (
      <ErrorBlock>
        <p>
          The <strong>NumberList</strong> block requires{' '}
          <strong>NumberListItem</strong> as children. For example:
        </p>
        <p>
          <ErrorCode>
            {`<NumberList>
              <NumberList.Item>Something</NumberList.Item>
            </NumberList>`}
          </ErrorCode>
        </p>
      </ErrorBlock>
    );
  }

  return (
    <div>
      <UniversalGridder as='ul'>{children}</UniversalGridder>
    </div>
  );
}

interface NumberListItemProps {
  value: number;
  unit: string;
  title: string;
  children: React.ReactNode;
}

function NumberListItem({ value, unit, title, children }: NumberListItemProps) {
  return (
    <MosaicItem>
      <TileNumber title={title} value={value} unit={unit}>
        {children}
      </TileNumber>
    </MosaicItem>
  );
}

NumberListItem.displayName = 'NumberListItem';

NumberList.Item = NumberListItem;

export { FeatureList, NumberList };
